import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { isChunkLoadError, reloadForNewBuild } from '@/lib/chunkReload'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info)
    // A tab that outlived a UI release asks for a chunk the new build renamed: reload once.
    if (isChunkLoadError(error)) reloadForNewBuild()
  }

  handleRetry = () => {
    // Re-rendering would request the same missing chunk; only a reload loads the new build.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-white">Something went wrong</h3>
          <p className="text-sm text-gray-400 mt-1 max-w-md">{this.state.error?.message}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={this.handleRetry}
          >
            Try again
          </Button>
        </div>
      )
    }
    return this.props.children
  }
}
